import { SiteBatchUpdateEntityToBatchUpdateDto } from "src/app/features/Site/services/mappers/SiteBatchUpdateEntityToBatchUpdateDto";
import { SiteListToDto } from "src/app/features/Site/services/mappers/SiteListToDto";
import type { SiteBatchUpdateEntity } from "src/app/features/Site/types/SiteBatchUpdateEntity";
import type { ImportResult } from "../../../../../components/FeatureButtons/types";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { baseAuthApi } from "../../../../../core/service/baseApi";
import type { FormMutation } from "../../../../../core/service/types/FormMutation";
import type { FormQuery } from "../../../../../core/service/types/FormQuery";
import type { Dto } from "../../../../../core/types/be/Dto";
import type { SiteCopyEntity } from "../../types/SiteCopyEntity";
import type { SiteEntity } from "../../types/SiteEntity";
import { SiteCopyEntityToCopyDto } from "../mappers/SiteCopyEntityToCopyDto";
import { SiteDtoToEntity } from "../mappers/SiteDtoToEntity";
import { SiteEntityToDto } from "../mappers/SiteEntityToDto";

const SiteApi = baseAuthApi.injectEndpoints({
    endpoints: (builder) => ({
        SiteLoad: builder.query<SiteEntity, FormQuery>({
            providesTags: [ResourceName.SITES],
            query: (queryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.SITES}/${queryParams.entityId}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return SiteDtoToEntity(response);
            },
        }),
        SiteSave: builder.mutation<SiteEntity, FormMutation<SiteEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.SITES}`;
                const method = "POST";

                const body = SiteEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.SITES],
        }),
        SiteUpdate: builder.mutation<SiteEntity, FormMutation<SiteEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.SITES}/${mutation.data.id}`;
                const method = "PUT";

                const body = SiteEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.SITES],
        }),
        SiteCopy: builder.mutation<SiteEntity, FormMutation<SiteCopyEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.SITES}/${mutation.data.id}/${FORM_ACTION.COPY}`;
                const method = "POST";

                const body = SiteCopyEntityToCopyDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.SITES],
        }),
        SiteFinishDealerChange: builder.mutation<SiteEntity, FormMutation<SiteEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.SITES}/${mutation.data.id}/${FORM_ACTION.FINISH_DEALER_CHANGE}`;
                const method = "PUT";

                const body = SiteListToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: ["mainCache"],
        }),
        SiteBatchUpdate: builder.mutation<ImportResult, FormMutation<SiteBatchUpdateEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.SITES}/${FORM_ACTION.BATCH_UPDATE}`;
                const method = "PUT";

                const body = SiteBatchUpdateEntityToBatchUpdateDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            transformResponse: (response: { successCount?: number; failedCount?: number; errors?: string[] }): ImportResult => ({
                importSuccessCount: response.successCount,
                importFailedCount: response.failedCount,
                importErrors: response.errors,
            }),
            invalidatesTags: ["mainCache"],
        }),
    }),
});

export const {
    useSiteLoadQuery,
    useSiteSaveMutation,
    useSiteUpdateMutation,
    useSiteCopyMutation,
    useSiteFinishDealerChangeMutation,
    useSiteBatchUpdateMutation,
} = SiteApi;
